/**
 * Batch-research step status → the progress dot and its tooltip (CategoryResearch batch dialog).
 *
 * codex-reviewer on #6445 (minor, at aff253bc8): the route labels a spec-cache step `completed_partial` when the
 * refresh PRESERVED existing values (existing pattern keys kept, a frozen value conflicted, or an established row was
 * refused — routes/category-characteristics.js, stepLabel). That is a DIAGNOSTIC label on a successful step: the
 * category still counts as completed and the job status is unchanged. Before this module the dot fell through to the
 * gray "not run" default, so exactly the population this PR creates — every refresh over existing rows — read as
 * unfinished to the operator. One mapping, used by the page and pinned by a behavioural test, so a fourth status
 * cannot silently fall through again.
 */
export const BATCH_STEP_STATUS_DOT: Readonly<Record<string, string>> = Object.freeze({
  completed: 'bg-green-500',
  completed_empty: 'bg-green-500 ring-2 ring-slate-400',
  skipped_empty_inventory: 'bg-slate-500',
  // Supported results are available; the amber ring marks gaps or retained values.
  completed_partial: 'bg-green-500 ring-2 ring-amber-400',
  completed_with_gaps: 'bg-green-500 ring-2 ring-amber-400',
  running: 'bg-blue-500 animate-pulse',
  failed: 'bg-red-500',
});

export const BATCH_STEP_STATUS_DOT_DEFAULT = 'bg-gray-300';

export function batchStepDotClass(status: string | undefined | null): string {
  if (!status) return BATCH_STEP_STATUS_DOT_DEFAULT;
  return BATCH_STEP_STATUS_DOT[status] ?? BATCH_STEP_STATUS_DOT_DEFAULT;
}

export function batchStepTitle(step: string, status: string | undefined | null): string {
  if (status === 'completed_empty') return `${step}: research selected no attributes; existing data preserved`;
  if (status === 'skipped_empty_inventory') return `${step}: skipped because this research selected no attributes; no provider calls`;
  if (status === 'completed_partial' || status === 'completed_with_gaps') {
    return `${step}: completed with gaps — supported results are available; some values may be retained or unresolved`;
  }
  return `${step}: ${status ?? 'pending'}`;
}

// A category can deliver usable research and still have gaps without being a
// failed category. Count each recorded category once; retain older job totals.
export function batchResearchGapCount(job: {
  failedCategories?: number;
  progressDetails?: Record<string, {status?: string; steps?: Record<string, unknown>}>;
}): number {
  const gapStatuses = new Set(['failed', 'blocked', 'outcome_unknown', 'completed_partial', 'completed_with_gaps']);
  const recorded = Object.values(job.progressDetails || {}).filter(entry =>
    gapStatuses.has(entry.status || '') || Object.values(entry.steps || {}).some(status => gapStatuses.has(String(status)))
  ).length;
  return Math.max(job.failedCategories || 0, recorded);
}
